import React, { useState, useRef, useMemo, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { getDisplayName } from '../../utils/admin';
import { Avatar } from '../Avatar';
import { channelsRef, onlineUsersRef, gestureState, MEDIA_BUNDLE_PREFIX, CHAT_LINK_PREFIX, CHAT_LINK_SUFFIX, DOWNLOAD_SHARE_PREFIX, isMobile, isTouchDevice } from '../../constants';
import { MediaBundle } from './MediaBundle';
import { TextFormatToolbar } from './TextFormatToolbar';
import { applyFormatToTextarea } from './applyFormat';
import { enrichMentions, stripMentionTags } from '../../utils/mentions';
import { LinkPreview } from './LinkPreview';
import { isMediaBundle, parseMediaBundle } from '../../utils/media';
import { openFromMessageClick } from '../../utils/fileViewer';
import { renderMessageHtml } from '../../utils/messageHtml';
import { isEditableMessage, parseDownloadShare, plainTextOf, copyToClipboard, messageLink } from '../../utils/messageActions';
import { formatBytes } from '../../utils/parallelDownload';
import { REACTION_EMOJIS } from './MessageSheet';
import { toast } from '../Toast';
import { Icon } from '../Icon';
import { uiZoom } from '../../utils/uiScale';

const URL_RE = /https?:\/\/[^\s<>"'）」】\]）]+/i;
const extractFirstUrl = (text) => {
    const m = URL_RE.exec(text);
    return m ? m[0].replace(/[.,!?;:]+$/, '') : null;
};

const LONG_PRESS_MS = 450;
const SWIPE_REPLY_PX = 56;

// "채팅에 공유"된 다운로드 항목 카드
const DownloadShareCard = ({ share }) => (
    <div className="share-card">
        <div className="share-card-icon">📦</div>
        <div className="share-card-body">
            <div className="share-card-title">{share.text}</div>
            <div className="share-card-meta">
                {share.size ? formatBytes(share.size) : (share.external ? '외부 링크' : '배포 파일')}
                {share.updatedAt ? ` · ${new Date(share.updatedAt).toLocaleDateString()}` : ''}
            </div>
            {share.note && <div className="share-card-note">{share.note}</div>}
        </div>
        <Link className="share-card-btn" to={`/download?item=${share.id}`}>받으러 가기</Link>
    </div>
);

export const Message = React.memo(({
    msg, isGrouped, clientData, isAdmin, setReply, onDelete, onRollback, onMediaClick, onImageLoad, onCopyLink,
    channelId, onNavigateToMsg, onEdit, onMentionUser, sendReact, onEditingChange, onScrollToMsg,
    isSaved, onToggleSave, autoEditNonce, readCount, onOpenSheet,
}) => {
    const date = new Date(msg.timestamp);
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const fullTimeStr = date.toLocaleString('ko-KR', { dateStyle: 'full', timeStyle: 'short' });
    const reactionEntries = msg.reactions ? Object.entries(msg.reactions).filter(([, users]) => Array.isArray(users) && users.length) : [];
    const displayUsername = getDisplayName(msg.username);
    const canEdit = clientData.userId === msg.userId && isEditableMessage(msg);
    const canDelete = isAdmin || clientData.userId === msg.userId;
    const rawMessage = typeof msg.message === 'string' ? msg.message : String(msg.message ?? '');
    const canCopyText = !msg.is_deleted && !isMediaBundle(rawMessage);

    const [isEditing, setIsEditing] = useState(false);
    const editRef = useRef(null);
    const [showReactPicker, setShowReactPicker] = useState(false);
    const [showMore, setShowMore] = useState(false); // false | 'down' | 'up' (화면 아래쪽이면 위로 펼침)
    const actionsRef = useRef(null);
    const [ctxMenu, setCtxMenu] = useState(null); // PC 우클릭 메뉴: { x, y } (마우스 위치)
    const ctxMenuRef = useRef(null);
    const [swipeX, setSwipeX] = useState(0);
    const touchRef = useRef(null);
    const suppressClickRef = useRef(false);

    // 반응 선택창·더보기 메뉴 바깥을 누르거나 Esc 로 닫기
    const menuOpen = showReactPicker || showMore;
    useEffect(() => {
        if (!menuOpen) return undefined;
        const closeAll = () => { setShowReactPicker(false); setShowMore(false); };
        const close = (e) => { if (!actionsRef.current?.contains(e.target)) closeAll(); };
        const onKey = (e) => { if (e.key === 'Escape') closeAll(); };
        document.addEventListener('mousedown', close);
        document.addEventListener('touchstart', close, { passive: true });
        window.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', close); document.removeEventListener('touchstart', close); window.removeEventListener('keydown', onKey); };
    }, [menuOpen]);

    // 우클릭 메뉴: 바깥 클릭·Esc·스크롤·창 크기 변경 시 닫기
    useEffect(() => {
        if (!ctxMenu) return undefined;
        const close = () => setCtxMenu(null);
        const onDown = (e) => { if (!ctxMenuRef.current?.contains(e.target)) close(); };
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('mousedown', onDown);
        window.addEventListener('keydown', onKey);
        window.addEventListener('resize', close);
        document.addEventListener('scroll', close, true);
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', close);
            document.removeEventListener('scroll', close, true);
        };
    }, [ctxMenu]);
    // 화면 밖으로 넘치면 마우스 위치 기준 왼쪽·위로 펼친다
    useLayoutEffect(() => {
        const el = ctxMenuRef.current;
        if (!ctxMenu || !el) return;
        // 화면 확대(zoom) 중이면 style 의 px 는 확대 전 단위라 화면 픽셀을 나눠서 계산
        const z = uiZoom();
        const rect = el.getBoundingClientRect();
        const width = rect.width / z, height = rect.height / z, vw = window.innerWidth / z, vh = window.innerHeight / z;
        const left = ctxMenu.x + width > vw - 8 ? Math.max(8, ctxMenu.x - width) : ctxMenu.x;
        const top = ctxMenu.y + height > vh - 8 ? Math.max(8, ctxMenu.y - height) : ctxMenu.y;
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
        el.style.visibility = 'visible';
    }, [ctxMenu]);

    const resizeEdit = (el) => { el.style.height = 'auto'; el.style.height = Math.max(90, el.scrollHeight) + 'px'; };
    const submitEditRef = useRef(null);

    const startEdit = () => {
        if (!canEdit) return;
        setIsEditing(true);
        if (onEditingChange) onEditingChange(() => submitEditRef.current?.());
        setTimeout(() => {
            const el = editRef.current;
            if (!el) return;
            // 편집 텍스트에서 @[userId|username] → @username 으로 변환해 표시
            el.value = stripMentionTags(msg.message);
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
            resizeEdit(el);
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 30);
    };
    const cancelEdit = () => { setIsEditing(false); if (onEditingChange) onEditingChange(null); };
    const submitEdit = () => {
        const raw = (editRef.current ? editRef.current.value : '').trim();
        if (!raw) return;
        // 편집 시에도 @username → @[userId|username] 변환
        const enriched = enrichMentions(raw, onlineUsersRef.current);
        if (enriched !== msg.message) onEdit(msg.id, enriched);
        setIsEditing(false);
        if (onEditingChange) onEditingChange(null);
        setTimeout(() => onScrollToMsg && onScrollToMsg(msg.id), 80);
    };
    submitEditRef.current = submitEdit;

    // 입력창에서 ↑ 키 → 내 마지막 메시지 수정
    useEffect(() => {
        if (autoEditNonce) startEdit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoEditNonce]);

    const handleEditFormat = (type) => {
        const textarea = editRef.current;
        if (!textarea) return;
        applyFormatToTextarea(textarea, type, textarea.value, () => {});
        setTimeout(() => { if (textarea) resizeEdit(textarea); }, 0);
    };

    const isMeMentioned = !msg.is_deleted && (msg.message.includes(`@[${clientData.userId}|`) || msg.message.includes(`@${clientData.username}`));
    const isGlobalMention = !msg.is_deleted && (msg.message.includes('@everyone') || msg.message.includes('@here'));

    const handleReply = () => {
        const replyText = msg.message.startsWith(MEDIA_BUNDLE_PREFIX) ? '(사진/동영상)' : plainTextOf(msg) || DOMPurify.sanitize(msg.message).replace(/<[^>]*>/g, '');
        setReply({ id: msg.id, username: msg.username, message: replyText });
    };

    const handleQuoteClick = () => {
        if (!msg.replyTo) return;
        const el = document.querySelector(`[data-message-id='${msg.replyTo.id}']`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('highlight'); setTimeout(() => el.classList.remove('highlight'), 1500); }
        else onNavigateToMsg?.(channelId, msg.replyTo.id);
    };

    const handleCopyLink = async () => {
        if (await copyToClipboard(messageLink(channelId, msg.id))) onCopyLink?.();
    };
    const handleCopyText = async () => {
        if (await copyToClipboard(plainTextOf(msg))) toast.success('텍스트를 복사했습니다.');
        else toast.error('복사에 실패했습니다.');
    };

    // ── 모바일: 길게 누르기 → 메뉴, 오른쪽으로 밀기 → 답장 ──
    const openSheet = () => onOpenSheet?.({
        msg,
        actions: {
            react: !msg.is_deleted && sendReact ? (emoji) => sendReact(msg.id, emoji) : null,
            reply: !msg.is_deleted ? handleReply : null,
            copyText: canCopyText ? handleCopyText : null,
            edit: canEdit ? startEdit : null,
            toggleSave: !msg.is_deleted && onToggleSave ? () => onToggleSave(msg) : null,
            isSaved,
            copyLink: handleCopyLink,
            rollback: isAdmin && msg.is_deleted ? () => onRollback(msg.id, false) : null,
            remove: canDelete && !msg.is_deleted ? () => onDelete(msg.id, false) : null,
        },
    });

    const touchHandlers = isTouchDevice && onOpenSheet ? {
        onTouchStart: (e) => {
            if (e.touches.length !== 1 || isEditing || e.target.closest('textarea, input, .edit-area, video, .code-block-container pre')) return;
            const t = e.touches[0];
            const state = { x: t.clientX, y: t.clientY, dx: 0, swiping: false, fired: false, timer: null };
            state.timer = setTimeout(() => {
                state.fired = true;
                navigator.vibrate?.(12);
                openSheet();
            }, LONG_PRESS_MS);
            touchRef.current = state;
        },
        onTouchMove: (e) => {
            const s = touchRef.current;
            if (!s) return;
            const t = e.touches[0];
            const dx = t.clientX - s.x, dy = t.clientY - s.y;
            if (Math.abs(dx) > 10 || Math.abs(dy) > 10) clearTimeout(s.timer);
            // 답장은 오른쪽 → 왼쪽으로 민다 (2026-10-05 마브 요청).
            // 화면 오른쪽 가장자리에서 시작한 스와이프는 접속자 목록 열기에 양보
            if (!s.swiping && !s.fired && !msg.is_deleted && s.x < window.innerWidth - 24 && dx < -16 && Math.abs(dx) > Math.abs(dy) * 1.6) s.swiping = true;
            if (s.swiping) { s.dx = Math.max(-80, Math.min(0, dx)); setSwipeX(s.dx); }
        },
        onTouchEnd: () => {
            const s = touchRef.current;
            touchRef.current = null;
            if (!s) return;
            clearTimeout(s.timer);
            if (s.swiping) {
                // 같은 손짓으로 화면 쪽 "왼쪽으로 밀면 접속자 목록 열기"가 같이 일어나지 않게 표시해 둔다
                gestureState.replySwipe = true;
                if (s.dx <= -SWIPE_REPLY_PX) { navigator.vibrate?.(10); handleReply(); }
                setSwipeX(0);
            }
            if (s.fired) {
                // 길게 누른 뒤 손을 뗄 때 발생하는 클릭(반응 토글·사진 열기 등)은 무시
                suppressClickRef.current = true;
                setTimeout(() => { suppressClickRef.current = false; }, 500);
            }
        },
        onTouchCancel: () => { const s = touchRef.current; if (s) clearTimeout(s.timer); touchRef.current = null; setSwipeX(0); },
        onContextMenu: (e) => { if (!e.target.closest('a')) e.preventDefault(); },
        onClickCapture: (e) => { if (suppressClickRef.current) { e.preventDefault(); e.stopPropagation(); } },
    } : {};

    // PC: 메시지를 우클릭하면 더보기 메뉴를 마우스 위치에 띄운다.
    // 링크·사진·동영상·입력창 위, 글자를 드래그해 선택한 상태에서는 브라우저 기본 메뉴(복사 등)를 그대로 둔다.
    const onContextMenu = (e) => {
        if (touchHandlers.onContextMenu) return touchHandlers.onContextMenu(e);
        if (isEditing || e.target.closest('a, img, video, textarea, input')) return;
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && e.currentTarget.contains(sel.anchorNode)) return;
        e.preventDefault();
        setShowReactPicker(false);
        setShowMore(false);
        const z = uiZoom();
        setCtxMenu({ x: e.clientX / z, y: e.clientY / z });
    };

    // 더보기 메뉴와 우클릭 메뉴가 같은 항목을 쓴다
    const renderMenuItems = (close) => (
        <>
            {canCopyText && <button role="menuitem" className="menu-item" onClick={() => { close(); handleCopyText(); }}><Icon name="copy" size={16} /><span>텍스트 복사</span></button>}
            {onToggleSave && <button role="menuitem" className="menu-item" onClick={() => { close(); onToggleSave(msg); }}><Icon name="bookmark" size={16} /><span>{isSaved ? '저장 취소' : '메시지 저장'}</span></button>}
            <button role="menuitem" className="menu-item" onClick={() => { close(); handleCopyLink(); }}><Icon name="link" size={16} /><span>링크 복사</span></button>
            {canDelete && (
                <>
                    <div className="menu-divider" />
                    <button role="menuitem" className="menu-item danger" title="Shift 를 누른 채 누르면 확인 없이 삭제" onClick={(e) => { close(); onDelete(msg.id, e.shiftKey); }}><Icon name="trash" size={16} /><span>삭제</span></button>
                </>
            )}
        </>
    );

    const closeCtx = () => setCtxMenu(null);
    const contextMenu = ctxMenu && createPortal(
        <div className="message-more-menu context" role="menu" ref={ctxMenuRef}
            style={{ left: ctxMenu.x, top: ctxMenu.y, visibility: 'hidden' }}
            onContextMenu={(e) => e.preventDefault()}
            // 포털이어도 React 이벤트는 메시지 쪽으로 올라가므로 여기서 멈춘다
            onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            {msg.is_deleted ? (
                <>
                    {isAdmin && <button role="menuitem" className="menu-item" onClick={(e) => { closeCtx(); onRollback(msg.id, e.shiftKey); }}><Icon name="undo" size={16} /><span>복구</span></button>}
                    <button role="menuitem" className="menu-item" onClick={() => { closeCtx(); handleCopyLink(); }}><Icon name="link" size={16} /><span>링크 복사</span></button>
                </>
            ) : (
                <>
                    {sendReact && (
                        <div className="context-reactions">
                            {REACTION_EMOJIS.slice(0, 6).map(emoji => (
                                <button key={emoji} className="react-picker-btn" title={emoji} onClick={() => { closeCtx(); sendReact(msg.id, emoji); }}>{emoji}</button>
                            ))}
                        </div>
                    )}
                    <button role="menuitem" className="menu-item" onClick={() => { closeCtx(); handleReply(); }}><Icon name="reply" size={16} /><span>답장</span></button>
                    {canEdit && <button role="menuitem" className="menu-item" onClick={() => { closeCtx(); startEdit(); }}><Icon name="edit" size={16} /><span>수정</span></button>}
                    <div className="menu-divider" />
                    {renderMenuItems(closeCtx)}
                </>
            )}
        </div>,
        document.body
    );

    let containerClass = 'message-container';
    if (isGrouped) containerClass += ' grouped';
    if (msg.is_deleted) {
        containerClass += msg.deleted_by === '관리자' ? ' deleted-by-admin' : ' deleted-by-user';
        if (isAdmin) containerClass += ' admin-archive-view';
    } else if (isMeMentioned || isGlobalMention) { containerClass += ' mentioned'; }
    if (swipeX) containerClass += ' swiping';

    // 마크다운 파싱·정화·멘션 처리는 비용이 크므로 본문이 바뀔 때만 다시 계산한다
    const mediaFiles = useMemo(() => parseMediaBundle(rawMessage), [rawMessage]);
    const share = useMemo(() => parseDownloadShare(rawMessage), [rawMessage]);
    const html = useMemo(
        () => (isMediaBundle(rawMessage) || rawMessage.startsWith(CHAT_LINK_PREFIX) || rawMessage.startsWith(DOWNLOAD_SHARE_PREFIX)
            ? null
            : renderMessageHtml(rawMessage, { channels: channelsRef.current, onlineUsers: onlineUsersRef.current })),
        [rawMessage]
    );
    const previewUrl = useMemo(() => (html && !msg.is_deleted ? extractFirstUrl(rawMessage) : null), [html, rawMessage, msg.is_deleted]);

    const renderBody = () => {
        if (isMediaBundle(rawMessage)) {
            if (!mediaFiles) return <div className="error-msg">미디어 로딩 실패</div>;
            return <MediaBundle files={mediaFiles} onOpenLightbox={(index) => onMediaClick(mediaFiles, index)} onImageLoad={() => onImageLoad && onImageLoad(msg.userId, msg.id)} />;
        }
        if (share) return <DownloadShareCard share={share} />;
        if (rawMessage.startsWith(CHAT_LINK_PREFIX)) {
            try {
                const link = JSON.parse(rawMessage.substring(CHAT_LINK_PREFIX.length, rawMessage.length - CHAT_LINK_SUFFIX.length));
                const preview = typeof link.message === 'string'
                    ? link.message.replace(/@@MEDIA_BUNDLE::.*?@@/g, '(사진/동영상)').replace(/<[^>]*>/g, '').substring(0, 80)
                    : '';
                return (
                    <div className="chat-link-preview" onClick={() => onNavigateToMsg && onNavigateToMsg(link.channelId, link.msgId)} title={`#${link.channelName} 채널로 이동`}>
                        <span className="chat-link-channel"># {link.channelName}</span>
                        <span className="chat-link-arrow">›</span>
                        <span className="chat-link-author">{link.username}</span>
                        <span className="chat-link-text">{preview || '(미디어)'}</span>
                    </div>
                );
            } catch {}
        }
        return (
            <>
                <div onClick={openFromMessageClick} dangerouslySetInnerHTML={{ __html: html ?? renderMessageHtml(rawMessage) }} />
                {previewUrl && <LinkPreview url={previewUrl} onLoad={onImageLoad ? () => onImageLoad(msg.userId, msg.id) : undefined} />}
            </>
        );
    };

    // 관리자는 삭제된 메시지도 본다. 예전에는 본문을 글자로만 보여 줘서 사진·동영상이 '(미디어)' 로만 나왔다 —
    // 삭제된 것을 확인하려고 보는 화면인데 정작 무엇이 삭제됐는지 볼 수 없었다. 그래서 원래 내용을 그대로 그린다.
    const renderContent = () => {
        if (isAdmin && msg.is_deleted) return (
            <span className="deleted-admin-view">
                <em className="deleted-admin-note">🗑️ {msg.deleted_by ? `${msg.deleted_by}에 의해 삭제됨` : '삭제됨'}</em>
                {renderBody()}
            </span>
        );
        return renderBody();
    };

    return (
        <div className={containerClass} data-message-id={msg.id} {...touchHandlers} onContextMenu={onContextMenu}>
            {swipeX < 0 && <div className="swipe-reply-hint" style={{ opacity: Math.min(1, -swipeX / SWIPE_REPLY_PX) }}><Icon name="reply" /></div>}
            <div className="message-swipe" style={swipeX ? { transform: `translateX(${swipeX}px)` } : undefined}>
                {/* 같은 사람이 이어서 보낸 메시지: 아바타 자리에 마우스를 올리면 보낸 시각 (슬랙·디스코드 방식) */}
                {isGrouped && <time className="grouped-time" dateTime={msg.timestamp} title={fullTimeStr}>{date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/^(오전|오후)\s*/, '')}</time>}
                {!isGrouped && <Avatar userId={msg.userId} name={displayUsername} onClick={() => onMentionUser && onMentionUser({ username: msg.username, userId: msg.userId })} title={`@${displayUsername} 멘션`} />}
                <div className="message-content">
                    {!isGrouped && (
                        <div className="message-header">
                            <strong style={{ cursor: onMentionUser ? 'pointer' : 'default' }} onClick={() => onMentionUser && onMentionUser({ username: msg.username, userId: msg.userId })} title={`@${displayUsername} 멘션`}>{displayUsername}</strong>
                            <time className="time" dateTime={msg.timestamp} title={fullTimeStr}>{timeStr}</time>
                            {msg.is_edited ? <span className="edited-label">(수정됨)</span> : null}
                            {isSaved ? <span className="saved-label" title="저장한 메시지">🔖</span> : null}
                        </div>
                    )}
                    <div className="message-body">
                        {msg.replyTo && (
                            <div className="reply-quote" onClick={handleQuoteClick}>
                                <div className="reply-bar"></div>
                                <Avatar className="reply-avatar-sm" userId={msg.replyTo.userId} name={getDisplayName(msg.replyTo.username)} />
                                <strong>{getDisplayName(msg.replyTo.username)}</strong>
                                <div className="reply-text">{DOMPurify.sanitize(msg.replyTo.message).replace(/<[^>]*>/g, '').substring(0, 100)}</div>
                            </div>
                        )}
                        {isEditing ? (
                            <div className="edit-area">
                                <TextFormatToolbar show={true} onFormat={handleEditFormat} isMobileToolbar={isMobile} />
                                <textarea ref={editRef} className="edit-textarea" defaultValue=""
                                    onChange={e => resizeEdit(e.target)}
                                    onKeyDown={e => {
                                        if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'y' || e.key === 'Z')) return;
                                        if (e.key === 'Escape') { cancelEdit(); return; }
                                        if (!isMobile && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEdit(); }
                                    }}
                                    style={{ minHeight: '90px' }} />
                                <div className="edit-actions">
                                    <span className="edit-hint">{isMobile ? '저장 버튼으로 완료 · Esc 취소' : 'Enter 저장 · Shift+Enter 줄바꿈 · Esc 취소'}</span>
                                    <button className="edit-save-btn" onClick={submitEdit}>저장</button>
                                    <button className="edit-cancel-btn" onClick={cancelEdit}>취소</button>
                                </div>
                            </div>
                        ) : renderContent()}
                    </div>
                    {!msg.is_deleted && reactionEntries.length > 0 && (
                        <div className="message-reactions">
                            {reactionEntries.map(([emoji, users]) => (
                                <button key={emoji} className={`like-btn${users.includes(clientData.username) ? ' liked' : ''}`}
                                    title={`${users.map(u => getDisplayName(u)).join(', ')}님이 반응했습니다.`}
                                    onClick={() => sendReact && sendReact(msg.id, emoji)}>
                                    {emoji} <span className="like-count">{users.length}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {readCount && (
                        <div className="read-count" title={readCount.names?.length ? `읽음: ${readCount.names.join(', ')}` : undefined}>
                            👁 {readCount.read}/{readCount.total}명 읽음
                        </div>
                    )}
                </div>
            </div>
            {/* PC 에서 메시지에 마우스를 올리면 나오는 도구: 반응 · 수정 · 답장 · 더보기(복사·저장·링크·삭제)
                예전엔 버튼 7개가 한 줄로 나와 무엇이 무엇인지 찾기 어려웠다 */}
            <div className={`message-actions${menuOpen ? ' picker-open' : ''}`} ref={actionsRef}>
                {(isAdmin && msg.is_deleted) ? <button className="action-btn rollback-btn" title="복구" aria-label="복구" onClick={(e) => onRollback(msg.id, e.shiftKey)}><Icon name="undo" size={18} /></button> : null}
                {msg.is_deleted ? <button className="action-btn" title="메시지 링크 복사" aria-label="메시지 링크 복사" onClick={handleCopyLink}><Icon name="link" size={18} /></button> : (
                    <>
                        <button className={`action-btn${showReactPicker ? ' active' : ''}`} title="반응 추가" aria-label="반응 추가" onClick={() => { setShowMore(false); setShowReactPicker(p => !p); }}><Icon name="smile" size={18} /></button>
                        {/* 반응 선택창은 도구 모음 전체(오른쪽 끝) 기준으로 펼쳐 화면 밖으로 잘리지 않게 한다 */}
                        {showReactPicker && (
                            <div className="react-picker-popup">
                                {REACTION_EMOJIS.map(e => (
                                    <button key={e} className="react-picker-btn" onClick={() => { sendReact && sendReact(msg.id, e); setShowReactPicker(false); setTimeout(() => onScrollToMsg && onScrollToMsg(msg.id), 150); }}>{e}</button>
                                ))}
                            </div>
                        )}
                        {/* 수정은 자주 쓰므로 더보기 안이 아니라 바로 보이게: 반응 · 수정 · 답장 순 (2026-10-28 마브 요청) */}
                        {canEdit && !isEditing && <button className="action-btn" title="수정 (↑)" aria-label="수정" onClick={() => { setShowReactPicker(false); setShowMore(false); startEdit(); }}><Icon name="edit" size={18} /></button>}
                        <button className="action-btn" title="답장" aria-label="답장" onClick={handleReply}><Icon name="reply" size={18} /></button>
                        <button className={`action-btn${showMore ? ' active' : ''}`} title="더보기" aria-label="더보기" aria-haspopup="menu" aria-expanded={!!showMore}
                            onClick={() => {
                                setShowReactPicker(false);
                                const bottom = actionsRef.current?.getBoundingClientRect().bottom || 0;
                                setShowMore(v => (v ? false : (bottom + 240 * uiZoom() > window.innerHeight ? 'up' : 'down')));
                            }}><Icon name="more" size={18} /></button>
                        {showMore && (
                            <div className={`message-more-menu ${showMore}`} role="menu">
                                {renderMenuItems(() => setShowMore(false))}
                            </div>
                        )}
                    </>
                )}
            </div>
            {contextMenu}
        </div>
    );
});
